import {
    Button,
    ColumnType,
    DataGrid,
    Form,
    GridColumnsConfigProvider,
    requiredRule,
    ValidatedInput,
} from '@finbeat/react-toolkit';
import { getSchedule } from 'axios/tariffsApi';
import DataTimeText from 'components/Common/DataTime';
import Money from 'components/Common/Money';
import useApi from 'hooks/useApi';
import { ExtendedTariffSchedule, TariffSchedule } from 'models/api/tariff';
import DataGridProp from 'models/dataGridProp';
import { useGeneralSettingsContext } from 'providers/GeneralSettingsProvider';
import { Key, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { Alert, Card, CardBody, Col, Row } from 'reactstrap';
import './TariffsModal.scss';

type CalcMaster = {
    groupName: string;
    annuityPaymentSum: number;
    interestSum: number;
    principalDebtSum: number;
    otherCommissionsSum: number;
};

type MasterRow = CalcMaster & { type: 'MASTER'; expanded: boolean };

type TariffScheduleForm = {
    term: number | null;
    amount: number | null;
};

const defaultValues: TariffScheduleForm = {
    term: null,
    amount: null,
};

type SummaryRows = {
    annuityPaymentSum: number;
    interestSum: number;
    principalDebtSum: number;
    otherCommissionsSum: number;
};

type ScheduleRow = TariffSchedule & { type: 'SHEDULE' };

type TariffScheduleTable = DataGridProp & (MasterRow | ScheduleRow);

const TariffsModalSchedule = ({
    tariffPlanId,
    apr,
}: {
    tariffPlanId: Key | null;
    apr: number | null;
}) => {
    const api = useApi();
    const { t } = useTranslation();
    const [schedule, setSchedule] = useState<ExtendedTariffSchedule>({
        annualSumm: 0,
        effectiveSumm: 0,
        schedules: [],
    });
    const [scheduleTable, setScheduleTable] = useState<TariffScheduleTable[] | null>(null);
    const { dateFormat } = useGeneralSettingsContext();

    const { isLoading, mutate: fetchSchedule } = useMutation(
        'tariffSchedule',
        ({ term, amount }: TariffScheduleForm) => getSchedule(api, tariffPlanId, term, amount, apr),
        {
            onSuccess: (data) => {
                setSchedule(data);

                setScheduleTable(
                    data.schedules.map<TariffScheduleTable>((x) => ({ type: 'SHEDULE', ...x }))
                );
            },
        }
    );

    const columns: ColumnType<TariffScheduleTable, SummaryRows>[] = [
        {
            key: 'dt',
            width: 100,
            draggable: false,
            name: <Trans i18nKey={'BackOfficeDt'} />,
            formatter: ({ row }) => (
                <>{row.type === 'SHEDULE' && <DataTimeText value={row.dt} format={dateFormat} />}</>
            ),
            summaryFormatter: () => <>{t('BackOfficeTotal')}</>,
            summaryCellClass: 'tariffs-modal__table-summary',
            colSpan: (args) => {
                if (args.type === 'SUMMARY') {
                    return 2;
                }
            },
        },
        {
            key: 'parentService',
            draggable: false,
            name: <Trans i18nKey={'BackOfficeParentService'} />,
            width: 110,
            formatter: ({ row }) => <>{row.type === 'SHEDULE' && row.parentService}</>,
        },
        {
            key: 'annuityPayment',
            draggable: false,
            width: 130,
            name: <Trans i18nKey={'BackOfficeAnnuityPayment'} />,
            formatter: ({ row }) => (
                <>{row.type === 'SHEDULE' && <Money value={row.annuityPayment} />}</>
            ),
            summaryFormatter: ({ row }) => <Money value={row.annuityPaymentSum} />,
            colSpan: (args) => {
                if (args.type === 'SUMMARY') {
                    return 4;
                }
            },
            summaryCellClass: 'tariffs-modal__table-summary',
        },
        {
            key: 'mainDebt',
            width: 110,
            draggable: false,
            name: <Trans i18nKey={'BackOfficeMainDebt'} />,
            formatter: ({ row }) =>
                row.type === 'SHEDULE' ? <Money value={row.mainDebt} /> : <></>,
        },
        {
            key: 'mainDebtSd',
            width: 120,
            draggable: false,
            name: <Trans i18nKey={'BackOfficeMainDebtSd'} />,
            formatter: ({ row }) =>
                row.type === 'SHEDULE' ? <Money value={row.mainDebtSd} /> : <></>,
        },
        {
            key: 'mainDebtEd',
            width: 120,
            draggable: false,
            name: <Trans i18nKey={'BackOfficeMainDebtEd'} />,
            formatter: ({ row }) =>
                row.type === 'SHEDULE' ? <Money value={row.mainDebtEd} /> : <></>,
        },
        {
            key: 'interest',
            width: 100,
            draggable: false,
            name: <Trans i18nKey={'BackOfficeInterest'} />,
            formatter: ({ row }) =>
                row.type === 'SHEDULE' ? <Money value={row.interest} /> : <></>,
            summaryFormatter: ({ row }) => <Money value={row.interestSum} />,
            summaryCellClass: 'tariffs-modal__table-summary',
        },
        {
            key: 'principalDebt',
            draggable: false,
            name: <Trans i18nKey={'BackOfficePrincipalDebt'} />,
            formatter: ({ row }) =>
                row.type === 'SHEDULE' ? <Money value={row.principalDebt} /> : <></>,
            summaryFormatter: ({ row }) => <Money value={row.principalDebtSum} />,
            summaryCellClass: 'tariffs-modal__table-summary',
        },
        {
            key: 'otherCommissions',
            draggable: false,
            name: <Trans i18nKey={'BackOfficeOtherCommissions'} />,
            formatter: ({ row }) =>
                row.type === 'SHEDULE' ? <Money value={row.otherCommissions} /> : <></>,
            summaryFormatter: ({ row }) => <Money value={row.otherCommissionsSum} />,
            summaryCellClass: 'tariffs-modal__table-summary',
        },
    ];

    const summaryRows = useMemo(() => {
        const summary = schedule.schedules.reduce(
            (sum, item) => {
                sum.interestSum += item.interest ?? 0;
                sum.principalDebtSum += item.principalDebt ?? 0;
                sum.otherCommissionsSum += item.otherCommissions ?? 0;
                return sum;
            },
            {
                annuityPaymentSum: 0,
                interestSum: 0,
                principalDebtSum: 0,
                otherCommissionsSum: 0,
            }
        );

        return [
            {
                annuityPaymentSum: summary.interestSum + summary?.principalDebtSum,
                interestSum: summary.interestSum,
                principalDebtSum: summary.principalDebtSum,
                otherCommissionsSum: summary.otherCommissionsSum,
            },
        ];
    }, [schedule.schedules]);

    return (
        <Col>
            <Form<TariffScheduleForm>
                onSubmit={(data) => fetchSchedule(data)}
                defaultValues={defaultValues}
                clearAfterSubmit={false}
            >
                <Row>
                    <Col xs="5">
                        <ValidatedInput<TariffScheduleForm>
                            name="amount"
                            type="number"
                            placeholder={t('BackOfficeLoanAmount')}
                            rules={{ required: requiredRule }}
                            isFloating={false}
                        />
                    </Col>
                    <Col xs="5">
                        <ValidatedInput<TariffScheduleForm>
                            name="term"
                            type="number"
                            placeholder={t('BackOfficeTerm')}
                            rules={{ required: requiredRule }}
                            isFloating={false}
                        />
                    </Col>
                    <Col>
                        <Button className="mb-3 w-100" outline type="submit" isPending={isLoading}>
                            <Trans i18nKey="BackOfficeCalculate" />
                        </Button>
                    </Col>
                </Row>
            </Form>

            {schedule &&
                scheduleTable &&
                (scheduleTable.length ? (
                    <>
                        <Row>
                            <Col className="schedule-table">
                                <GridColumnsConfigProvider<TariffScheduleTable, SummaryRows>
                                    storagePath="tariffs-schedule-grid"
                                    initColumns={columns}
                                >
                                    <DataGrid<TariffScheduleTable>
                                        reactDataGreedProps={{
                                            defaultColumnOptions: {
                                                sortable: false,
                                                resizable: false,
                                            },
                                            summaryRows: summaryRows,
                                        }}
                                        rows={scheduleTable}
                                        loading={isLoading}
                                    />
                                </GridColumnsConfigProvider>
                            </Col>
                        </Row>

                        <Row>
                            <Col className="col-md-8 mx-auto">
                                <Card className="border-0">
                                    <CardBody>
                                        <table className="table table-sm small mb-0">
                                            <tbody>
                                                <tr className="border-0">
                                                    <td className="font-weight-bold border-top-0">
                                                        <Trans
                                                            i18nKey={
                                                                'BackOfficeEffectiveInterestRate'
                                                            }
                                                        />
                                                    </td>
                                                    <td className="font-weight-bold border-top-0">
                                                        <Money value={schedule.effectiveSumm} />
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td className="font-weight-bold border-top-0">
                                                        <Trans i18nKey={'BackOfficeAnnualRate'} />
                                                    </td>
                                                    <td className="font-weight-bold border-top-0">
                                                        <Money value={schedule.annualSumm} />
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    </>
                ) : (
                    <Alert color="danger" isOpen={schedule?.schedules?.length === 0}>
                        <div className="d-flex gap-1">
                            <strong>{t('BackOfficeError')}!</strong>
                            <span>{t('BackOfficeImpossiblePaymentScheduleMsg')}</span>
                        </div>
                    </Alert>
                ))}
        </Col>
    );
};

export default TariffsModalSchedule;
